/**
 * @fileoverview Authentication context provider using custom token-based auth.
 * Provides user identity, organization info, role checks, and auth actions
 * to all child components via React context.
 *
 * In test environments (NODE_ENV=test), the provider skips real initialization
 * and provides a default authenticated state so existing tests pass without modification.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { getToken, getRefreshToken, setTokens, clearTokens } from '../services/keycloak';
import axios from 'axios';
import { setActiveOrgId } from '../services/api';

/**
 * @typedef {Object} AuthUser
 * @property {string} id - User UUID
 * @property {string} email - User email
 * @property {string} display_name - Display name
 */

/**
 * @typedef {Object} AuthOrg
 * @property {string} id - Organization UUID
 * @property {string} name - Organization name
 * @property {string} role - User's role in this org
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {boolean} initialized - Whether auth initialization is complete
 * @property {boolean} authenticated - Whether the user is authenticated
 * @property {AuthUser|null} user - Current user info
 * @property {AuthOrg|null} activeOrg - Currently active organization
 * @property {AuthOrg[]} orgs - All organizations the user belongs to
 * @property {boolean} isSuperAdmin - Whether user has super_admin role
 * @property {string|null} adminPath - Admin navigation path if applicable
 * @property {Function} customLogin - Log in with email and password
 * @property {Function} logout - Log out and clear tokens
 * @property {Function} switchOrg - Switch active organization
 * @property {boolean} needsOnboarding - Whether user needs to complete onboarding
 * @property {Function} getToken - Get current access token
 * @property {boolean} requiresTermsAcceptance - Whether user must re-accept updated terms
 * @property {string|null} currentTermsVersion - Current system terms version ISO date
 * @property {Function} acceptTerms - Record re-acceptance of updated terms
 */

export const AuthContext = createContext(null);

/** Default state used in test environments to keep existing tests passing. */
const TEST_DEFAULT_STATE = {
  initialized: true,
  authenticated: true,
  user: { id: 'test-user-id', email: 'test@example.com', display_name: 'Test User' },
  activeOrg: { id: 'test-org-id', name: 'Test Org', role: 'admin', slug: 'test-org' },
  orgs: [{ id: 'test-org-id', name: 'Test Org', role: 'admin', slug: 'test-org' }],
  isSuperAdmin: false,
  emailVerified: true,
  needsOnboarding: false,
  adminPath: null,
  adminApiPrefix: null,
  impersonatedOrg: null,
  requiresTermsAcceptance: false,
  currentTermsVersion: null,
  login: () => {},
  logout: () => {},
  customLogin: () => Promise.resolve({ success: true }),
  switchOrg: () => Promise.resolve(),
  startImpersonation: () => Promise.resolve(),
  stopImpersonation: () => Promise.resolve(),
  acceptTerms: () => Promise.resolve(),
  getToken: () => 'test-token',
};

/**
 * Detect whether we are running in a test environment.
 *
 * @returns {boolean} True if running under Jest/test runner
 */
function isTestEnv() {
  return process.env.NODE_ENV === 'test';
}

/**
 * AuthProvider component that wraps the application with authentication context.
 * In production, checks for a stored refresh token and bootstraps the session.
 * In test environments, provides a default authenticated state.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} [props.testOverrides] - Override auth state values (testing only)
 * @returns {JSX.Element} Provider wrapping children
 */
export function AuthProvider({ children, testOverrides }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [activeOrg, setActiveOrg] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminPath, setAdminPath] = useState(null);
  const [adminApiPrefix, setAdminApiPrefix] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [impersonatedOrg, setImpersonatedOrg] = useState(null);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);
  const [currentTermsVersion, setCurrentTermsVersion] = useState(null);
  const tokenRefreshTimer = useRef(null);
  const restoringImpersonation = useRef(false);

  /**
   * Fetch user and org data from /api/auth/me after successful authentication.
   * Sets user, orgs, activeOrg, isSuperAdmin, adminPath, and terms acceptance state.
   */
  const fetchAuthMe = useCallback(async () => {
    try {
      const token = getToken();
      const { data } = await axios.get('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser({
        id: data.id,
        username: data.username,
        display_name: data.display_name,
        email: data.email,
      });
      setOrgs(data.organizations || []);
      setIsSuperAdmin(data.is_super_admin || false);
      setEmailVerified(data.email_verified !== false);
      setAdminPath(data.admin_path || null);
      setAdminApiPrefix(data.admin_api_prefix || null);

      // Terms re-acceptance state (LEGAL-05/LEGAL-06)
      // Stored so TermsReacceptanceModal can gate navigation when outdated.
      // current_terms_version is always present so TermsReacceptanceModal can
      // pass the correct version to POST /api/auth/accept-terms.
      setRequiresTermsAcceptance(data.requires_terms_acceptance || false);
      setCurrentTermsVersion(data.current_terms_version || null);

      // Determine if user needs onboarding:
      // true when user has no orgs, or only the default org with 0 businesses
      // Super admins never need onboarding
      const userOrgs = data.organizations || [];
      const onboarding = !data.is_super_admin && (userOrgs.length === 0 ||
        (userOrgs.length === 1 && userOrgs[0].name === 'Default Organization' &&
         (!userOrgs[0].business_count || userOrgs[0].business_count === 0)));
      setNeedsOnboarding(onboarding);

      // Set active org from response or first org
      if (data.active_org) {
        setActiveOrg(data.active_org);
        setActiveOrgId(String(data.active_org.id));
      } else if (data.organizations && data.organizations.length > 0) {
        setActiveOrg(data.organizations[0]);
        setActiveOrgId(String(data.organizations[0].id));
      }
    } catch (err) {
      console.error('Failed to fetch auth user info:', err);
    }
  }, []);

  /**
   * Start impersonating an organization (super admin only).
   * Calls switch-org API, sets impersonatedOrg state, updates activeOrg,
   * and persists the impersonation to sessionStorage for page refresh survival.
   *
   * @param {Object} org - The organization to impersonate
   * @param {string} org.id - Organization ID
   * @param {string} org.name - Organization name
   * @param {string} org.slug - Organization slug
   * @returns {Promise<void>}
   */
  const startImpersonation = useCallback(async (org) => {
    try {
      const token = getToken();
      await axios.post('/api/auth/switch-org', { organization_id: org.id }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-ID': org.id,
        },
      });
      setImpersonatedOrg(org);
      setActiveOrg({ id: org.id, name: org.name, slug: org.slug, role: 'admin' });
      setActiveOrgId(String(org.id));
      sessionStorage.setItem('impersonatedOrg', JSON.stringify(org));
    } catch (err) {
      console.error('Failed to start impersonation:', err);
      throw err;
    }
  }, []);

  /**
   * Stop impersonating an organization.
   * Clears impersonation state and sessionStorage, then re-fetches auth/me
   * to restore the user's real organization data.
   *
   * @returns {Promise<void>}
   */
  const stopImpersonation = useCallback(async () => {
    setImpersonatedOrg(null);
    sessionStorage.removeItem('impersonatedOrg');
    await fetchAuthMe();
  }, [fetchAuthMe]);

  /**
   * Set up a periodic token refresh to keep the session alive.
   * Posts the refresh token to /api/auth/refresh every 60 seconds.
   */
  const setupTokenRefresh = useCallback(() => {
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
    }
    tokenRefreshTimer.current = setInterval(async () => {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const { data } = await axios.post('/api/auth/refresh', {
            refresh_token: refreshToken,
          });
          setTokens(data);
        } catch {
          // Token refresh failed — user needs to re-login
          clearTokens();
          setAuthenticated(false);
        }
      }
    }, 60000);
  }, []);

  useEffect(() => {
    // In test environments, skip real init entirely
    if (isTestEnv()) {
      setInitialized(true);
      setAuthenticated(testOverrides?.authenticated !== undefined ? testOverrides.authenticated : true);
      if (testOverrides?.user) setUser(testOverrides.user);
      else setUser(TEST_DEFAULT_STATE.user);
      if (testOverrides?.activeOrg) setActiveOrg(testOverrides.activeOrg);
      else setActiveOrg(TEST_DEFAULT_STATE.activeOrg);
      if (testOverrides?.orgs) setOrgs(testOverrides.orgs);
      else setOrgs(TEST_DEFAULT_STATE.orgs);
      if (testOverrides?.isSuperAdmin !== undefined) setIsSuperAdmin(testOverrides.isSuperAdmin);
      if (testOverrides?.needsOnboarding !== undefined) setNeedsOnboarding(testOverrides.needsOnboarding);
      if (testOverrides?.emailVerified !== undefined) setEmailVerified(testOverrides.emailVerified);
      if (testOverrides?.adminPath) setAdminPath(testOverrides.adminPath);
      if (testOverrides?.adminApiPrefix) setAdminApiPrefix(testOverrides.adminApiPrefix);
      if (testOverrides?.impersonatedOrg !== undefined) setImpersonatedOrg(testOverrides.impersonatedOrg);
      if (testOverrides?.requiresTermsAcceptance !== undefined) setRequiresTermsAcceptance(testOverrides.requiresTermsAcceptance);
      if (testOverrides?.currentTermsVersion !== undefined) setCurrentTermsVersion(testOverrides.currentTermsVersion);
      return;
    }

    let cancelled = false;

    /**
     * Bootstrap the session by checking for a stored refresh token.
     * If found, attempts to refresh and loads user data. Otherwise marks
     * as initialized but unauthenticated.
     */
    async function init() {
      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          // Attempt to restore session using stored refresh token
          const { data } = await axios.post('/api/auth/refresh', {
            refresh_token: refreshToken,
          });
          if (cancelled) return;

          setTokens(data);
          setAuthenticated(true);
          await fetchAuthMe();
          setupTokenRefresh();

          // Restore impersonation from sessionStorage after auth/me succeeds
          if (!restoringImpersonation.current) {
            const stored = sessionStorage.getItem('impersonatedOrg');
            if (stored) {
              restoringImpersonation.current = true;
              try {
                const storedOrg = JSON.parse(stored);
                await startImpersonation(storedOrg);
              } catch (e) {
                console.error('Failed to restore impersonation:', e);
                sessionStorage.removeItem('impersonatedOrg');
              } finally {
                restoringImpersonation.current = false;
              }
            }
          }
        } else {
          // No refresh token — user is not logged in
          if (cancelled) return;
        }
      } catch (err) {
        // Refresh failed — clear stale tokens
        clearTokens();
        console.error('Session restore failed:', err);
      } finally {
        if (!cancelled) {
          setInitialized(true);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      if (tokenRefreshTimer.current) {
        clearInterval(tokenRefreshTimer.current);
      }
    };
  }, [fetchAuthMe, setupTokenRefresh, startImpersonation, testOverrides]);

  // Fallback: if authenticated but user data wasn't fetched (e.g., HMR or race condition),
  // retry fetching auth/me.
  useEffect(() => {
    if (isTestEnv()) return;
    if (initialized && authenticated && !user) {
      const token = getToken();
      if (token) {
        fetchAuthMe();
      }
    }
  }, [initialized, authenticated, user, fetchAuthMe]);

  /**
   * Log in with email and password credentials.
   * Sends a POST to /api/auth/login, stores the returned tokens,
   * fetches user data, and starts the refresh timer.
   *
   * @param {string} email - User email address
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, error?: string}>} Login result
   */
  const customLogin = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post('/api/auth/login', { email, password });
      setTokens(data);
      await fetchAuthMe();
      setAuthenticated(true);
      setupTokenRefresh();
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, error: message };
    }
  }, [fetchAuthMe, setupTokenRefresh]);

  /**
   * Log out the current user.
   * Notifies the backend, clears tokens, and resets auth state.
   * Navigation to /landing is left to the caller.
   */
  const logout = useCallback(async () => {
    try {
      const token = getToken();
      if (token) {
        await axios.post('/api/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Logout API failure is non-fatal
    }
    clearTokens();
    setAuthenticated(false);
    setUser(null);
    setActiveOrg(null);
    setOrgs([]);
    setIsSuperAdmin(false);
    setEmailVerified(true);
    setAdminPath(null);
    setAdminApiPrefix(null);
    setNeedsOnboarding(false);
    setImpersonatedOrg(null);
    setRequiresTermsAcceptance(false);
    setCurrentTermsVersion(null);
    sessionStorage.removeItem('impersonatedOrg');
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }
  }, []);

  /**
   * Switch the active organization by calling the backend endpoint.
   *
   * @param {string} orgId - The organization ID to switch to
   * @returns {Promise<void>}
   */
  const switchOrg = useCallback(async (orgId) => {
    try {
      const token = getToken();
      await axios.post('/api/auth/switch-org', { organization_id: orgId }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Organization-ID': orgId,
        },
      });
      const newOrg = orgs.find(o => o.id === orgId)
        || (impersonatedOrg && String(impersonatedOrg.id) === String(orgId) ? { ...impersonatedOrg, role: 'admin' } : null);
      if (newOrg) {
        setActiveOrg(newOrg);
        setActiveOrgId(String(orgId));
      }
    } catch (err) {
      console.error('Failed to switch organization:', err);
      throw err;
    }
  }, [orgs, impersonatedOrg]);

  /**
   * Record the user's acceptance of updated Terms of Service.
   *
   * Calls POST /api/auth/accept-terms with the current terms version, then
   * re-fetches /api/auth/me so requires_terms_acceptance is updated to false.
   * The TermsReacceptanceModal dismisses automatically when
   * requiresTermsAcceptance becomes false.
   *
   * @returns {Promise<void>}
   * @throws {Error} If the API call fails
   */
  const acceptTerms = useCallback(async () => {
    try {
      const token = getToken();
      await axios.post('/api/auth/accept-terms', { terms_version: currentTermsVersion }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Re-fetch auth/me to clear requires_terms_acceptance
      await fetchAuthMe();
    } catch (err) {
      console.error('Failed to accept terms:', err);
      throw err;
    }
  }, [currentTermsVersion, fetchAuthMe]);

  /**
   * Get the current access token from the token manager.
   *
   * @returns {string|null} The JWT access token or null
   */
  const getTokenFn = useCallback(() => {
    if (isTestEnv()) return 'test-token';
    return getToken();
  }, []);

  // When impersonating an org not in the user's real orgs, inject it so
  // OrgResolver can find it by slug during navigation.
  const effectiveOrgs = impersonatedOrg && !orgs.find(o => String(o.id) === String(impersonatedOrg.id))
    ? [...orgs, { id: impersonatedOrg.id, name: impersonatedOrg.name, slug: impersonatedOrg.slug, role: 'admin' }]
    : orgs;

  const contextValue = {
    initialized,
    authenticated,
    user,
    activeOrg,
    orgs: effectiveOrgs,
    isSuperAdmin,
    emailVerified,
    needsOnboarding,
    adminPath,
    adminApiPrefix,
    impersonatedOrg,
    requiresTermsAcceptance,
    currentTermsVersion,
    login: (isTestEnv() && testOverrides?.login) || (() => {}),
    customLogin: (isTestEnv() && testOverrides?.customLogin) || customLogin,
    logout: (isTestEnv() && testOverrides?.logout) || logout,
    switchOrg: (isTestEnv() && testOverrides?.switchOrg) || switchOrg,
    startImpersonation: (isTestEnv() && testOverrides?.startImpersonation) || startImpersonation,
    stopImpersonation: (isTestEnv() && testOverrides?.stopImpersonation) || stopImpersonation,
    acceptTerms: (isTestEnv() && testOverrides?.acceptTerms) || acceptTerms,
    getToken: getTokenFn,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  testOverrides: PropTypes.object,
};

/**
 * Hook to access the AuthContext value.
 *
 * @returns {AuthContextValue} The auth context
 * @throws {Error} If used outside of an AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
